import { useId, useState } from 'react';

import { Button, Group, Stack, Text, Title } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import type { RegistryDocumentDigest } from '@/types/registry-documents';
import { CorporateInformation, DigestAppendix, FinancialInformation } from './DigestSections';
import { hasCorporateInformation, TEMPLATE_WORDS } from './digestTemplate';

/** What the fold says of itself. */
export const COMBINED_VIEW_WORDS = {
  toggle: 'Combined view',
  note: "The order's documents merged into one set of tables, for reading side by side. The report takes the sections above, never this.",
} as const;

interface DigestCombinedViewProps {
  digest: RegistryDocumentDigest;
  registry: string;
  companyName: string;
  deliveredAt: string | null;
}

/**
 * The order-wide tables of a per-document digest, folded away under a
 * "Combined view" toggle: Corporate Information merged from every
 * register extract, Financial Information, the gathered caveats and the
 * sources appendix. The analyst still finds the merged tables useful for
 * reading an order with several extracts side by side; nothing merged is
 * what goes into the report, and the fold says so.
 */
export function DigestCombinedView({
  digest,
  registry,
  companyName,
  deliveredAt,
}: Readonly<DigestCombinedViewProps>) {
  const [opened, setOpened] = useState(false);
  const bodyId = useId();
  return (
    <Stack gap="xs" data-testid="registry-digest-combined">
      <Group gap="xs">
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={opened ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
          aria-expanded={opened}
          aria-controls={bodyId}
          onClick={() => setOpened((open) => !open)}
          data-testid="registry-digest-combined-toggle"
        >
          {COMBINED_VIEW_WORDS.toggle}
        </Button>
      </Group>
      {opened && (
        <Stack gap="sm" id={bodyId} data-testid="registry-digest-combined-body">
          <Text size="xs" c="dimmed">
            {COMBINED_VIEW_WORDS.note}
          </Text>
          {hasCorporateInformation(digest) && (
            <>
              <Title order={6}>{TEMPLATE_WORDS.title}</Title>
              <CorporateInformation
                corporate={digest}
                registry={registry}
                companyName={companyName}
              />
            </>
          )}
          {digest.financials && (
            <>
              <Title order={6}>{digest.financials.title}</Title>
              <FinancialInformation financials={digest.financials} registry={registry} />
            </>
          )}
          <DigestAppendix digest={digest} deliveredAt={deliveredAt} />
        </Stack>
      )}
    </Stack>
  );
}
